import { useCallback, useEffect, useState, useRef } from "react";
import { Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import ReactHlsPlayer from 'react-hls-player';
import { isSafari } from 'react-device-detect';

const mediaHeight = 500;

export const ImageView = ({ url, alt }: { url: string, alt: string }) => {
    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-evenly' }}>
            <img
                src={url}
                alt={`${alt}-media`}
                height={mediaHeight}
            />
        </div>
    )
}

export const VideoView = ({ url }: { url: string }) => {
    const playerRef = useRef<HTMLVideoElement>(null!);

    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-evenly' }}>
            {isSafari ? (
                <video height={mediaHeight} loop playsInline controls>
                    <source src={url} type="application/vnd.apple.mpegurl" />
                </video>
            ) : (
                <ReactHlsPlayer playerRef={playerRef} src={url} height={mediaHeight} loop controls />
            )}
        </div>

    )
}

type MediaViewType = {
    title: string;
    urls: string[];
    open?: boolean;
    setOpen?: (v: boolean) => void;
}

export const MediaView = ({ title, urls, open, setOpen }: MediaViewType) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (open) {
            setIndex(0);
        }
    }, [open])

    const handleNextMedia = useCallback(() => {
        if (index === urls.length - 1) {
            setIndex(0);
        } else {
            setIndex(index + 1);
        }
    }, [index, urls.length]);

    const handlePrevMedia = useCallback(() => {
        if (index === 0) {
            setIndex(urls.length - 1);
        } else {
            setIndex(index - 1);
        }
    }, [index, urls.length]);

    const getMedia = useCallback(() => {
        const url = urls[index];
        return (
            <div style={{ width: "100%" }}>
                {!url ? (
                    <Typography height={mediaHeight} display="flex" alignItems="center" justifyContent="center" >
                        No media to display
                    </Typography>
                ) : url.endsWith("m3u8") ? (
                    <VideoView url={url} />
                ) : (
                    <ImageView url={url} alt={title} />
                )}
            </div>
        )
    }, [urls, index, title]);

    const mediaView = useCallback(() => {
        return (
            <Box display="flex" alignItems="center">
                <IconButton onClick={handlePrevMedia} sx={{ height: 50 }} disabled={urls.length <= 1}>
                    <ChevronLeft fontSize="large" />
                </IconButton>
                {getMedia()}
                <IconButton onClick={handleNextMedia} sx={{ height: 50 }} disabled={urls.length <= 1}>
                    <ChevronRight fontSize="large" />
                </IconButton>
            </Box>
        )
    }, [getMedia, handlePrevMedia, handleNextMedia, urls.length]);

    if (open !== undefined && setOpen !== undefined) {
        return (
            <Dialog
                open={open}
                onClose={() => setOpen ? setOpen(false) : {}}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
                    {mediaView()}
                </DialogContent>
            </Dialog>
        )
    }
    
    return mediaView();
}
